import { useState, useEffect, useCallback, useRef } from 'react';
import { getFirestore, collection, doc, getDoc, getDocs, addDoc, updateDoc, setDoc, orderBy, query, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { app } from '../../firebase/firebaseConfig';

export function useSupportMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [conversationExists, setConversationExists] = useState(false);
  const [accountUUID, setAccountUUID] = useState(null);

  const db = getFirestore(app);
  const auth = getAuth(app);

  // Refs to track subscriptions
  const conversationUnsubRef = useRef(null);
  const messagesUnsubRef = useRef(null);

  // Get accountUUID from token on mount
  useEffect(() => {
    const getUUID = async () => {
      const user = auth.currentUser;
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        const tokenResult = await user.getIdTokenResult();
        const uuid = tokenResult.claims.accountUUID;
        setAccountUUID(uuid);
      } catch (err) {
        console.error('Error getting accountUUID:', err);
        setError('Failed to authenticate');
        setLoading(false);
      }
    };
    getUUID();
  }, [auth]);

  // Subscribe to conversation and messages when accountUUID is available
  useEffect(() => {
    if (!accountUUID) return;

    setLoading(true);
    setError(null);

    // Subscribe to conversation document
    const conversationRef = doc(db, 'supportConversations', accountUUID);
    conversationUnsubRef.current = onSnapshot(
      conversationRef,
      (docSnap) => {
        if (docSnap.exists()) {
          setConversationExists(true);
          const data = docSnap.data();
          setUnreadCount(data.unreadByUser || 0);
        } else {
          setConversationExists(false);
          setUnreadCount(0);
        }
      },
      (err) => {
        console.error('Error subscribing to conversation:', err);
        // Don't set error for permission denied on non-existent doc
        if (err.code !== 'permission-denied') {
          setError('Failed to load conversation');
        }
      }
    );

    // Subscribe to messages
    const messagesRef = collection(db, 'supportConversations', accountUUID, 'messages');
    const messagesQuery = query(messagesRef, orderBy('createdAt', 'asc'));

    messagesUnsubRef.current = onSnapshot(
      messagesQuery,
      (snapshot) => {
        const messagesList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || new Date()
        }));
        setMessages(messagesList);
        setLoading(false);
      },
      (err) => {
        console.error('Error subscribing to messages:', err);
        // Permission denied is expected if conversation doesn't exist yet
        if (err.code !== 'permission-denied') {
          setError('Failed to load messages');
        }
        setLoading(false);
      }
    );

    // Cleanup subscriptions on unmount or accountUUID change
    return () => {
      if (conversationUnsubRef.current) {
        conversationUnsubRef.current();
      }
      if (messagesUnsubRef.current) {
        messagesUnsubRef.current();
      }
    };
  }, [db, accountUUID]);

  const sendMessage = useCallback(async (content, images = [], preGeneratedId = null) => {
    const trimmedContent = content.trim();
    if ((!trimmedContent && images.length === 0) || !accountUUID) return;

    setSending(true);
    setError(null);

    // Compute preview for conversation doc
    let preview;
    if (trimmedContent) {
      preview = trimmedContent.substring(0, 100);
      if (images.length > 0) preview += ` [+${images.length} image${images.length > 1 ? 's' : ''}]`;
    } else {
      preview = images.length === 1 ? '[Image]' : `[${images.length} Images]`;
    }

    // Optimistic update - add message to local state immediately
    const tempId = 'temp-' + Date.now();
    const optimisticMessage = {
      id: tempId,
      content: trimmedContent,
      senderType: 'user',
      senderEmail: auth.currentUser?.email,
      createdAt: new Date(),
      readAt: null,
      pending: true,
      ...(images.length > 0 && { images })
    };
    setMessages(prev => [...prev, optimisticMessage]);

    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Not authenticated');
      }

      const conversationRef = doc(db, 'supportConversations', accountUUID);

      // Check if conversation exists
      const conversationSnap = await getDoc(conversationRef);

      if (!conversationSnap.exists()) {
        // Create new conversation
        await setDoc(conversationRef, {
          createdAt: serverTimestamp(),
          lastMessageAt: serverTimestamp(),
          lastMessagePreview: preview,
          unreadByAdmin: 1,
          unreadByUser: 0,
          userEmail: user.email,
          status: 'open'
        });
      } else {
        // Update existing conversation
        const currentData = conversationSnap.data();
        await updateDoc(conversationRef, {
          lastMessageAt: serverTimestamp(),
          lastMessagePreview: preview,
          unreadByAdmin: (currentData.unreadByAdmin || 0) + 1,
          status: 'open'
        });
      }

      // Build message document
      const messageData = {
        content: trimmedContent,
        senderType: 'user',
        senderEmail: user.email,
        createdAt: serverTimestamp(),
        readAt: null,
        ...(images.length > 0 && { images })
      };

      // Add message to subcollection (use pre-generated ID if provided)
      const messagesRef = collection(db, 'supportConversations', accountUUID, 'messages');
      if (preGeneratedId) {
        await setDoc(doc(messagesRef, preGeneratedId), messageData);
      } else {
        await addDoc(messagesRef, messageData);
      }

      // Remove optimistic message (real one will come via onSnapshot)
      setMessages(prev => prev.filter(m => m.id !== tempId));

    } catch (err) {
      console.error('Error sending message:', err);
      // Remove optimistic message on error
      setMessages(prev => prev.filter(m => m.id !== tempId));
      setError('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  }, [db, auth, accountUUID]);

  const markAsRead = useCallback(async () => {
    if (!accountUUID) return;

    try {
      const conversationRef = doc(db, 'supportConversations', accountUUID);
      const conversationSnap = await getDoc(conversationRef);

      if (conversationSnap.exists() && conversationSnap.data().unreadByUser > 0) {
        // Update conversation unread count
        await updateDoc(conversationRef, {
          unreadByUser: 0,
          lastReadByUserAt: serverTimestamp()
        });

        // Also mark individual admin messages as read (for double ticks on admin side)
        const messagesRef = collection(db, 'supportConversations', accountUUID, 'messages');
        const messagesQuery = query(messagesRef, orderBy('createdAt', 'asc'));
        const messagesSnap = await getDocs(messagesQuery);

        const now = serverTimestamp();
        for (const messageDoc of messagesSnap.docs) {
          const messageData = messageDoc.data();
          // Only mark admin messages that haven't been read yet
          if (messageData.senderType === 'admin' && !messageData.readAt) {
            await updateDoc(doc(db, 'supportConversations', accountUUID, 'messages', messageDoc.id), {
              readAt: now
            });
          }
        }
      }
    } catch (err) {
      console.error('Error marking messages as read:', err);
    }
  }, [db, accountUUID]);

  const fetchUnreadCount = useCallback(async () => {
    if (!accountUUID) return 0;

    try {
      const conversationRef = doc(db, 'supportConversations', accountUUID);
      const conversationSnap = await getDoc(conversationRef);

      if (conversationSnap.exists()) {
        const count = conversationSnap.data().unreadByUser || 0;
        setUnreadCount(count);
        return count;
      }
      return 0;
    } catch (err) {
      console.error('Error fetching unread count:', err);
      return 0;
    }
  }, [db, accountUUID]);

  // Manual refresh function (not usually needed with real-time)
  const refreshMessages = useCallback(async () => {
    if (!accountUUID) return;

    try {
      const messagesRef = collection(db, 'supportConversations', accountUUID, 'messages');
      const messagesQuery = query(messagesRef, orderBy('createdAt', 'asc'));
      const messagesSnap = await getDocs(messagesQuery);

      const messagesList = messagesSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }));
      setMessages(messagesList);
    } catch (err) {
      console.error('Error refreshing messages:', err);
    }
  }, [db, accountUUID]);

  return {
    messages,
    loading,
    error,
    sending,
    unreadCount,
    conversationExists,
    accountUUID,
    sendMessage,
    markAsRead,
    fetchUnreadCount,
    refreshMessages,
    clearError: () => setError(null)
  };
}
